import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { motion, useViewportScroll } from 'framer-motion';
import { useSelector } from 'react-redux';
import CatchReport from '../CatchReport';
import ApCrossing from '../ApCrossing';
import { useAnglerSession } from '../menu/useAnglerSession';
import { useMenuActions } from '../menu/useMenuActions';
import DesktopNav from './DesktopNav';
import MenuSheet from './MenuSheet';
import SiteLogoLink from './SiteLogoLink';
import './assets/css/site_header.css';

const ALWAYS_SHOWN_ABOVE_PX = 120;
const SCROLL_DIRECTION_THRESHOLD_PX = 8;

function SiteHeader({ isLoggedInState, loadingState }) {
  const { isLoggedIn, handleLogOut } = useAnglerSession(
    isLoggedInState,
    loadingState
  );
  const { showCatchReportDrawer } = useSelector((state) => state.Menu);
  const { scrollY, scrollYProgress } = useViewportScroll();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(
    () => window.scrollY >= ALWAYS_SHOWN_ABOVE_PX
  );
  const menuButtonRef = useRef(null);
  const directionChangeY = useRef(window.scrollY);
  const { openCatchReport, openCrossing } = useMenuActions(isLoggedIn);

  const contentVariant = scrolled ? 'dark' : 'light';

  const closeSheet = () => setSheetOpen(false);

  const linkActions = {
    logOut: handleLogOut,
    catchReport: () => {
      if (openCatchReport()) {
        closeSheet();
      }
    },
    crossing: () => {
      openCrossing();
      closeSheet();
    },
  };

  const handleLinkAction = (action) => linkActions[action]();

  // Hide while scrolling down and show again on scroll up, measured from the last
  // direction change so small jitters don't flicker it. Near the top it always shows
  // and stays transparent; further down it gets a backdrop so it reads over content.
  useEffect(
    () =>
      scrollY.onChange((y) => {
        setScrolled(y >= ALWAYS_SHOWN_ABOVE_PX);
        const distance = y - directionChangeY.current;
        if (y < ALWAYS_SHOWN_ABOVE_PX) {
          setHidden(false);
          directionChangeY.current = y;
        } else if (Math.abs(distance) > SCROLL_DIRECTION_THRESHOLD_PX) {
          setHidden(distance > 0);
          directionChangeY.current = y;
        }
      }),
    [scrollY]
  );

  return (
    <>
      <motion.div
        className='sh-progress sb-blue-bg-400'
        style={{ scaleX: scrollYProgress }}
        aria-hidden='true'
      />
      <header
        className={[
          'sh-header',
          scrolled && 'sh-header--scrolled',
          hidden && !sheetOpen && 'sh-header--hidden',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <SiteLogoLink variant={contentVariant} />
        <DesktopNav
          variant={contentVariant}
          isLoggedIn={isLoggedIn}
          onLinkAction={handleLinkAction}
        />
        <Button
          ref={menuButtonRef}
          className={[
            'sh-menu-button bg-white sb-blue-900 gSemiBold fs-16 brFull',
            scrolled && 'grey-border-300',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-expanded={sheetOpen}
          aria-controls='sh-menu-sheet'
          onClick={() => setSheetOpen(true)}
        >
          Menu <MenuOutlined aria-hidden='true' />
        </Button>
      </header>
      <MenuSheet
        id='sh-menu-sheet'
        open={sheetOpen}
        onClose={closeSheet}
        onAfterClose={() => menuButtonRef.current?.focus()}
        onLinkAction={handleLinkAction}
        isLoggedIn={isLoggedIn}
      />
      <CatchReport isOpen={showCatchReportDrawer} />
      <ApCrossing />
    </>
  );
}

export default SiteHeader;
